// Service quản lý ma trận đề kiểm tra

import { bankQuestionService, BankQuizData, BankQuestion } from '../bankQuestionService_moi'
import type { ExamMatrix, ExamMatrixRow, DangToan, QuestionCell, ValidationResult } from '../../types/exam-matrix'
import toast from 'react-hot-toast'

export class ExamMatrixService {
  private readonly STORAGE_KEY = 'exam_matrices'

  /**
   * Lưu ma trận vào localStorage
   */
  async saveMatrix(matrix: ExamMatrix): Promise<void> {
    try {
      const matrices = await this.getAllMatrices()

      // Tìm và update hoặc thêm mới
      const index = matrices.findIndex(m => m.id === matrix.id)
      if (index >= 0) {
        matrices[index] = { ...matrix, updatedAt: new Date() }
      } else {
        matrices.push(matrix)
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(matrices))
      console.log(`✅ Đã lưu ma trận: ${matrix.tenMaTran}`)
    } catch (error) {
      console.error('❌ Lỗi khi lưu ma trận:', error)
      throw error
    }
  }

  /**
   * Lấy tất cả ma trận đã lưu
   */
  async getAllMatrices(): Promise<ExamMatrix[]> {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY)
      if (!data) return []

      const matrices = JSON.parse(data)
      // Parse dates
      return matrices.map((m: any) => ({
        ...m,
        createdAt: new Date(m.createdAt),
        updatedAt: new Date(m.updatedAt)
      }))
    } catch (error) {
      console.error('❌ Lỗi khi load ma trận:', error)
      return []
    }
  }

  /**
   * Lấy 1 ma trận theo ID
   */
  async getMatrix(id: string): Promise<ExamMatrix | null> {
    const matrices = await this.getAllMatrices()
    return matrices.find(m => m.id === id) || null
  }

  /**
   * Xóa ma trận
   */
  async deleteMatrix(id: string): Promise<void> {
    const matrices = await this.getAllMatrices()
    const filtered = matrices.filter(m => m.id !== id)
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered))
    console.log(`🗑️ Đã xóa ma trận: ${id}`)
  }

  /**
   * Tạo đề thi từ ma trận
   */
  async generateExamFromMatrix(matrix: ExamMatrix): Promise<BankQuizData> {
    console.log(`🎯 Generating exam from matrix: ${matrix.tenMaTran}`)

    const allQuestions: BankQuestion[] = []

    try {
      // Loop qua từng row trong ma trận
      for (const row of matrix.rows) {
        console.log(`📝 Processing row: ${row.baiHoc}`)

        // Loop qua từng dạng toán
        for (const dang of row.cacDang) {
          console.log(`  📌 Processing dạng: ${dang.tenDang}`)

          // TNKQ - Nhiều lựa chọn (TN)
          const tnQuestions = await this.getQuestionsForDang(
            row, dang, 'TN', 'tnkq.nhieuLuaChon'
          )
          allQuestions.push(...tnQuestions)

          // TNKQ - Đúng Sai (DS)
          const dsQuestions = await this.getQuestionsForDang(
            row, dang, 'DS', 'tnkq.dungSai'
          )
          allQuestions.push(...dsQuestions)

          // TNKQ - Trả lời ngắn (TL)
          const tlQuestions = await this.getQuestionsForDang(
            row, dang, 'TL', 'tnkq.traLoiNgan'
          )
          allQuestions.push(...tlQuestions)

          // Tự luận (cũng là TL nhưng tag là TLU để phân biệt chấm điểm)
          const tuLuanQuestions = await this.getQuestionsForDang(
            row, dang, 'TL', 'tuLuan'
          )
          // Tag group TLU cho các câu tự luận
          tuLuanQuestions.forEach(q => {
            if (!q.examMetadata) q.examMetadata = {} as any
            q.examMetadata!.group = 'TLU'
          })
          allQuestions.push(...tuLuanQuestions)
        }
      }

      console.log(`✅ Generated ${allQuestions.length} questions total`)

      // Tính tổng điểm
      const tongDiem = this.calculateTotalScore(allQuestions)

      // Tạo quiz data
      const quizData: BankQuizData = {
        id: `matrix_exam_${matrix.id}_${Date.now()}`,
        tieuDe: matrix.tenMaTran,
        moTa: `${matrix.loaiKiemTra} - Lớp ${matrix.lop} - ${allQuestions.length} câu - ${tongDiem} điểm`,
        gioi_han_thoi_gian: matrix.thoiGianLamBai * 60, // Convert to seconds
        diem_dat: matrix.diemDat,
        cauHoi: allQuestions
      }

      toast.success(`Đã tạo đề thi: ${allQuestions.length} câu, ${tongDiem} điểm`)
      return quizData

    } catch (error) {
      console.error('❌ Error generating exam:', error)
      toast.error('Không thể tạo đề thi. Vui lòng kiểm tra lại ma trận.')
      throw error
    }
  }

  /**
   * Lấy câu hỏi cho một dạng toán cụ thể
   */
  private async getQuestionsForDang(
    row: ExamMatrixRow,
    dang: DangToan,
    loaiDB: 'TN' | 'DS' | 'TL',
    path: string  // 'tnkq.nhieuLuaChon' hoặc 'tuLuan'
  ): Promise<BankQuestion[]> {
    const questions: BankQuestion[] = []

    // Parse nested path
    const parts = path.split('.')
    let cells: any = dang
    for (const part of parts) {
      cells = cells[part]
    }

    // Lấy câu hỏi cho từng mức độ
    const mucDoList = ['biet', 'hieu', 'vanDung'] as const

    for (const mucDo of mucDoList) {
      const cell: QuestionCell = cells[mucDo]

      if (cell.soCauYeuCau > 0) {
        const qs = await bankQuestionService.getQuestionsByMatrixCell(
          row.lop,
          row.chuong,
          row.bai,
          loaiDB,
          mucDo,
          cell.soCauYeuCau,
          dang.tenDang  // Dạng toán (để mở rộng sau)
        )

        questions.push(...qs)
      }
    }

    return questions
  }

  /**
   * Tính tổng điểm của đề thi
   */
  private calculateTotalScore(questions: BankQuestion[]): number {
    return questions.reduce((total, q) => {
      let diem = 0

      // Xác định điểm dựa trên loại câu hỏi
      if (q.type === 'trac_nghiem_nhieu_lua_chon' || q.type === 'multiple_choice') {
        diem = 0.25
      } else if (q.type === 'dung_sai' || q.type === 'true_false') {
        diem = 1.0
      } else if (q.type === 'tinh_toan' || q.type === 'short_answer') {
        // Check if it's TLU (Tự luận) or TL (Trả lời ngắn)
        if (q.examMetadata?.group === 'TLU') {
          diem = 1.0 // Tự luận 1.0 điểm
        } else {
          diem = 0.5 // Trả lời ngắn 0.5 điểm
        }
      }

      return total + diem
    }, 0)
  }

  /**
   * Load số câu available cho toàn bộ ma trận
   */
  async loadAvailableQuestionsForMatrix(matrix: ExamMatrix): Promise<ExamMatrix> {
    console.log(`📊 Loading available questions for matrix...`)

    const updatedRows: ExamMatrixRow[] = []

    for (const row of matrix.rows) {
      const available = await bankQuestionService.getAvailableQuestionsForRow(
        row.lop,
        row.chuong,
        row.bai
      )

      // Update từng cell với số câu có sẵn
      const updatedDangs = row.cacDang.map(dang => ({
        ...dang,
        tnkq: {
          nhieuLuaChon: {
            biet: { ...dang.tnkq.nhieuLuaChon.biet, soCauCoSan: available['TN_biet'] || 0, isValid: dang.tnkq.nhieuLuaChon.biet.soCauYeuCau <= (available['TN_biet'] || 0) },
            hieu: { ...dang.tnkq.nhieuLuaChon.hieu, soCauCoSan: available['TN_hieu'] || 0, isValid: dang.tnkq.nhieuLuaChon.hieu.soCauYeuCau <= (available['TN_hieu'] || 0) },
            vanDung: { ...dang.tnkq.nhieuLuaChon.vanDung, soCauCoSan: available['TN_vanDung'] || 0, isValid: dang.tnkq.nhieuLuaChon.vanDung.soCauYeuCau <= (available['TN_vanDung'] || 0) }
          },
          dungSai: {
            biet: { ...dang.tnkq.dungSai.biet, soCauCoSan: available['DS_biet'] || 0, isValid: dang.tnkq.dungSai.biet.soCauYeuCau <= (available['DS_biet'] || 0) },
            hieu: { ...dang.tnkq.dungSai.hieu, soCauCoSan: available['DS_hieu'] || 0, isValid: dang.tnkq.dungSai.hieu.soCauYeuCau <= (available['DS_hieu'] || 0) },
            vanDung: { ...dang.tnkq.dungSai.vanDung, soCauCoSan: available['DS_vanDung'] || 0, isValid: dang.tnkq.dungSai.vanDung.soCauYeuCau <= (available['DS_vanDung'] || 0) }
          },
          traLoiNgan: {
            biet: { ...dang.tnkq.traLoiNgan.biet, soCauCoSan: available['TL_biet'] || 0, isValid: dang.tnkq.traLoiNgan.biet.soCauYeuCau <= (available['TL_biet'] || 0) },
            hieu: { ...dang.tnkq.traLoiNgan.hieu, soCauCoSan: available['TL_hieu'] || 0, isValid: dang.tnkq.traLoiNgan.hieu.soCauYeuCau <= (available['TL_hieu'] || 0) },
            vanDung: { ...dang.tnkq.traLoiNgan.vanDung, soCauCoSan: available['TL_vanDung'] || 0, isValid: dang.tnkq.traLoiNgan.vanDung.soCauYeuCau <= (available['TL_vanDung'] || 0) }
          }
        },
        tuLuan: {
          biet: { ...dang.tuLuan.biet, soCauCoSan: available['TL_biet'] || 0, isValid: dang.tuLuan.biet.soCauYeuCau <= (available['TL_biet'] || 0) },
          hieu: { ...dang.tuLuan.hieu, soCauCoSan: available['TL_hieu'] || 0, isValid: dang.tuLuan.hieu.soCauYeuCau <= (available['TL_hieu'] || 0) },
          vanDung: { ...dang.tuLuan.vanDung, soCauCoSan: available['TL_vanDung'] || 0, isValid: dang.tuLuan.vanDung.soCauYeuCau <= (available['TL_vanDung'] || 0) }
        }
      }))

      updatedRows.push({ ...row, cacDang: updatedDangs })
    }

    return { ...matrix, rows: updatedRows }
  }

  /**
   * Validate ma trận trước khi tạo đề
   */
  async validateMatrix(matrix: ExamMatrix): Promise<ValidationResult> {
    const errors: string[] = []
    const warnings: string[] = []

    if (!matrix.tenMaTran || matrix.tenMaTran.trim() === '') {
      errors.push('Chưa nhập tên ma trận')
    }

    if (matrix.rows.length === 0) {
      errors.push('Ma trận chưa có nội dung')
    }

    for (const row of matrix.rows) {
      for (const dang of row.cacDang) {
        // Check từng cell
        this.validateCells(dang.tnkq.nhieuLuaChon, 'Nhiều lựa chọn', row.baiHoc, dang.tenDang, errors)
        this.validateCells(dang.tnkq.dungSai, 'Đúng Sai', row.baiHoc, dang.tenDang, errors)
        this.validateCells(dang.tnkq.traLoiNgan, 'Trả lời ngắn', row.baiHoc, dang.tenDang, errors)
        this.validateCells(dang.tuLuan, 'Tự luận', row.baiHoc, dang.tenDang, errors)
      }
    }

    // Check tổng số câu
    const totalQuestions = matrix.rows.reduce((sum, row) => {
      return sum + row.cacDang.reduce((rowSum, dang) => {
        return rowSum + this.countDangQuestions(dang)
      }, 0)
    }, 0)

    if (totalQuestions === 0) {
      errors.push('Chưa có câu hỏi nào trong ma trận')
    }

    if (totalQuestions < 5) {
      warnings.push(`Chỉ có ${totalQuestions} câu hỏi, nên có ít nhất 10 câu`)
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    }
  }

  private validateCells(
    cells: { biet: QuestionCell; hieu: QuestionCell; vanDung: QuestionCell },
    loai: string,
    baiHoc: string,
    dangToan: string,
    errors: string[]
  ) {
    const mucDoNames = { biet: 'Biết', hieu: 'Hiểu', vanDung: 'Vận dụng' }

    for (const [mucDo, cell] of Object.entries(cells)) {
      if (cell.soCauYeuCau > cell.soCauCoSan) {
        errors.push(
          `${baiHoc} - ${dangToan} - ${loai} - ${mucDoNames[mucDo as keyof typeof mucDoNames]}: Yêu cầu ${cell.soCauYeuCau} câu nhưng chỉ có ${cell.soCauCoSan} câu`
        )
      }
    }
  }

  private countDangQuestions(dang: DangToan): number {
    let count = 0

    // TNKQ
    count += dang.tnkq.nhieuLuaChon.biet.soCauYeuCau
    count += dang.tnkq.nhieuLuaChon.hieu.soCauYeuCau
    count += dang.tnkq.nhieuLuaChon.vanDung.soCauYeuCau
    count += dang.tnkq.dungSai.biet.soCauYeuCau
    count += dang.tnkq.dungSai.hieu.soCauYeuCau
    count += dang.tnkq.dungSai.vanDung.soCauYeuCau
    count += dang.tnkq.traLoiNgan.biet.soCauYeuCau
    count += dang.tnkq.traLoiNgan.hieu.soCauYeuCau
    count += dang.tnkq.traLoiNgan.vanDung.soCauYeuCau

    // Tự luận
    count += dang.tuLuan.biet.soCauYeuCau
    count += dang.tuLuan.hieu.soCauYeuCau
    count += dang.tuLuan.vanDung.soCauYeuCau

    return count
  }
}

export const examMatrixService = new ExamMatrixService()

